"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useCommsPanel } from "../CommsProvider";
import { TimelineEntry } from "../timeline/TimelineEntry";
import { ActionBar, type ComposerType } from "../ActionBar";
import { SmsComposer } from "../composers/SmsComposer";
import { WhatsAppComposer } from "../composers/WhatsAppComposer";
import { EmailComposer } from "../composers/EmailComposer";
import { NoteComposer } from "../composers/NoteComposer";
import { CallComposer } from "../composers/CallComposer";

interface TimelineEntryData {
  id: string;
  entryType: string;
  subject: string | null;
  body: string | null;
  metadata: Record<string, unknown> | null;
  userName: string | null;
  status: string;
  createdAt: string;
}

export function TimelineTab() {
  const { activeLeadId, initialComposer, composerSeq, timelineRefreshSeq } = useCommsPanel();
  const [entries, setEntries] = useState<TimelineEntryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeComposer, setActiveComposer] = useState<ComposerType>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Switch composer whenever an action button is clicked (composerSeq ensures re-fire)
  useEffect(() => {
    if (initialComposer) {
      setActiveComposer(initialComposer as ComposerType);
    }
  }, [initialComposer, composerSeq]);

  const entriesRef = useRef<string>("");

  const fetchTimeline = useCallback(async (silent = false) => {
    if (!activeLeadId) return;
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`/api/leads/${activeLeadId}/timeline?limit=100`);
      if (res.ok) {
        const data = await res.json();
        const incoming = data.data ?? [];
        // Only update state if data actually changed (prevents flash on polling)
        const fingerprint = JSON.stringify(incoming.map((e: TimelineEntryData) => `${e.id}:${e.status}`));
        if (fingerprint !== entriesRef.current) {
          entriesRef.current = fingerprint;
          setEntries(incoming);
        }
      }
    } catch {
      // ignore
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeLeadId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline, timelineRefreshSeq]);

  // Poll silently for new entries every 5 seconds (inbound messages, delivery status updates)
  useEffect(() => {
    if (!activeLeadId) return;
    const interval = setInterval(() => fetchTimeline(true), 5000);
    return () => clearInterval(interval);
  }, [activeLeadId, fetchTimeline]);

  // Auto-scroll to bottom when entries change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries]);

  // Date grouping helper
  function formatDateHeader(dateStr: string): string {
    const d = new Date(dateStr);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return "Today";
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
  }

  // Group entries by date
  const grouped: { date: string; entries: TimelineEntryData[] }[] = [];
  for (const entry of entries) {
    const dateKey = formatDateHeader(entry.createdAt);
    const last = grouped[grouped.length - 1];
    if (last && last.date === dateKey) {
      last.entries.push(entry);
    } else {
      grouped.push({ date: dateKey, entries: [entry] });
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable timeline */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            Loading timeline...
          </div>
        ) : entries.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            No communication history yet
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.date}>
              <div className="flex items-center gap-2 py-2">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-[10px] font-medium text-gray-400">{group.date}</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
              <div className="space-y-2">
                {group.entries.map((entry) => (
                  <TimelineEntry key={entry.id} entry={entry} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Composer */}
      {activeComposer === "sms" && <SmsComposer onSent={() => { setActiveComposer(null); fetchTimeline(); }} />}
      {activeComposer === "whatsapp" && <WhatsAppComposer onSent={() => { setActiveComposer(null); fetchTimeline(); }} />}
      {activeComposer === "email" && <EmailComposer onSent={() => { setActiveComposer(null); fetchTimeline(); }} />}
      {activeComposer === "note" && <NoteComposer onSent={() => { setActiveComposer(null); fetchTimeline(); }} />}
      {activeComposer === "call" && <CallComposer onSent={() => { setActiveComposer(null); fetchTimeline(); }} />}

      {/* Action Bar */}
      <ActionBar activeComposer={activeComposer} onSetComposer={setActiveComposer} />
    </div>
  );
}
